import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { useLoginModal } from '../Context/LoginModalContext';
import { getSessionToken } from '../Services/services';
import {
  getCatalogOfferings,
  getMarketplaceCatalog,
  getRepairCapabilitiesCatalog,
  getRepairCapabilityItem,
  addRepairToCart,
} from '../api/inventory';
import { getFiles } from '../api/files';
import { track, logger } from '../utils/logger';
import { useApprovedAccess } from '../utils/useApprovedAccess';
import AppHeader from './AppHeader';
import {
  ImgPlaceholderSvg,
  dedupeFiles,
  fileVersionId,
  fmtPrice,
  isPartImage,
  loadBlobUrl,
} from './InventoryDetailsModal';

/**
 * Cell for one repair service level.
 *
 * A price replaces the tick only when the org displays repair pricing and that
 * service actually carries an amount — an offered service without a price still
 * shows a tick, and a service that is not offered shows a dash.
 */
function repairServiceCell(offered, amount, pricingDisplayed) {
  if (!offered) return '—';
  if (pricingDisplayed && amount != null) return fmtPrice(amount);
  return '✓';
}

/**
 * Group flat inventory rows by (part_number, description).
 * Each group carries: the first item's id for navigation, summed qty, and unique conditions.
 */
function groupPartRows(items) {
  const map = new Map();
  for (const item of items) {
    const key = `${item.part_number}||${item.description || ''}`;
    if (!map.has(key)) {
      map.set(key, {
        navId: item.id,           // id of first item — navigates to detail page
        part_number: item.part_number,
        description: item.description || '',
        conditions: [],
        total_qty: 0,
      });
    }
    const g = map.get(key);
    if (item.quantity != null) g.total_qty += Number(item.quantity) || 0;
    if (item.condition && !g.conditions.includes(item.condition)) {
      g.conditions.push(item.condition);
    }
  }
  return Array.from(map.values());
}

const RESULT_COUNT_CAP = 10_000;
const MAX_PAGES_SHOW_TOTAL = 500;

const TABS = { PARTS: 'parts', REPAIR: 'repair' };

const EMPTY_PAGE = { items: [], total_count: 0, page: 1, page_size: 25, has_more: false, total_count_is_exact: true };

export default function Marketplace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branding } = useBranding();
  const { openLogin } = useLoginModal();

  // Product detail is gated: guests and pending users can search and view
  // results, but cannot drill into a detail page (ACON access limit).
  const { allowed: canViewDetail, isGuest } = useApprovedAccess();
  const [accessNotice, setAccessNotice] = useState('');

  const openPartDetail = (navId) => {
    if (!canViewDetail) {
      if (isGuest) openLogin({ redirectPath: `/inventory/item/${encodeURIComponent(navId)}` });
      else setAccessNotice('Product details are available once your account is approved. You can keep searching and viewing results in the meantime.');
      return;
    }
    navigate(`/inventory/item/${encodeURIComponent(navId)}`);
  };

  const openRepairDetail = (id) => {
    if (!canViewDetail) {
      if (isGuest) openLogin({ redirectPath: '/catalog' });
      else setAccessNotice('Repair capability details are available once your account is approved. You can keep searching and viewing results in the meantime.');
      return;
    }
    setSelectedRepairId(id);
  };

  // ── Catalog availability ───────────────────────────────────────────────────
  const [offerings, setOfferings] = useState({ parts_catalog_offered: false, repair_capabilities_offered: false });
  const [offeringsLoaded, setOfferingsLoaded] = useState(false);

  useEffect(() => {
    getCatalogOfferings()
      .then((data) => setOfferings(data || { parts_catalog_offered: false, repair_capabilities_offered: false }))
      .catch(() => setOfferings({ parts_catalog_offered: false, repair_capabilities_offered: false }))
      .finally(() => setOfferingsLoaded(true));
  }, []);

  const bothOffered = offerings.parts_catalog_offered && offerings.repair_capabilities_offered;
  const onlyParts   = offerings.parts_catalog_offered && !offerings.repair_capabilities_offered;
  const onlyRepair  = !offerings.parts_catalog_offered && offerings.repair_capabilities_offered;
  const neitherOffered = !offerings.parts_catalog_offered && !offerings.repair_capabilities_offered;
  const repairPricingDisplayed = offerings.repair_pricing_displayed === true;

  // ── Tab state ──────────────────────────────────────────────────────────────
  const rawTab = searchParams.get('tab');
  const activeTab = bothOffered
    ? (rawTab === TABS.REPAIR ? TABS.REPAIR : TABS.PARTS)
    : (onlyRepair ? TABS.REPAIR : TABS.PARTS);

  // ── Search params ──────────────────────────────────────────────────────────
  const keyword = searchParams.get('keyword') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const catalogParams = useMemo(() => {
    const raw = keyword.trim();
    if (!raw) return { page, page_size: 25 };
    // Always search via `q` so the query matches description, alternate, and
    // part number (raw + stripped). Sending stripped_q alongside q would AND the
    // two clauses on the backend and break description-only matches.
    return { page, page_size: 25, q: raw };
  }, [keyword, page]);

  // ── Parts state ────────────────────────────────────────────────────────────
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsError, setPartsError] = useState('');
  const [partsCatalog, setPartsCatalog] = useState(EMPTY_PAGE);

  // ── Repair state ───────────────────────────────────────────────────────────
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairError, setRepairError] = useState('');
  const [repairCatalog, setRepairCatalog] = useState(EMPTY_PAGE);
  const [hoveredRepairId, setHoveredRepairId] = useState(null);
  const [selectedRepairId, setSelectedRepairId] = useState(null);

  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [focusedRowId, setFocusedRowId] = useState(null);

  // ── Fetch parts ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!offeringsLoaded || !offerings.parts_catalog_offered) return;
    if (activeTab !== TABS.PARTS) return;
    let cancelled = false;
    setPartsLoading(true);
    setPartsError('');
    getMarketplaceCatalog(catalogParams)
      .then((data) => {
        if (cancelled) return;
        setPartsCatalog({
          items: data?.items || [],
          total_count: data?.total_count ?? 0,
          page: data?.page ?? page,
          page_size: data?.page_size ?? 25,
          has_more: !!data?.has_more,
          total_count_is_exact: data?.total_count_is_exact !== false,
        });
        track('marketplace.search', {
          tab: 'parts',
          page,
          query: keyword.trim() || null,
          resultCount: data?.total_count ?? (data?.items?.length || 0),
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setPartsError(e?.message || 'Failed to load inventory');
        logger.error('marketplace.search_failed', { tab: 'parts', page, status: e?.status, message: e?.message });
      })
      .finally(() => { if (!cancelled) setPartsLoading(false); });
    return () => { cancelled = true; };
  }, [catalogParams, activeTab, offeringsLoaded, offerings.parts_catalog_offered]);

  // ── Fetch repair ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!offeringsLoaded || !offerings.repair_capabilities_offered) return;
    if (activeTab !== TABS.REPAIR) return;
    let cancelled = false;
    setRepairLoading(true);
    setRepairError('');
    getRepairCapabilitiesCatalog(catalogParams)
      .then((data) => {
        if (cancelled) return;
        setRepairCatalog({
          items: data?.items || [],
          total_count: data?.total_count ?? 0,
          page: data?.page ?? page,
          page_size: data?.page_size ?? 25,
          has_more: !!data?.has_more,
          total_count_is_exact: data?.total_count_is_exact !== false,
        });
        track('marketplace.search', {
          tab: 'repair',
          page,
          query: keyword.trim() || null,
          resultCount: data?.total_count ?? (data?.items?.length || 0),
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setRepairError(e?.message || 'Failed to load repair capabilities');
        logger.error('marketplace.search_failed', { tab: 'repair', page, status: e?.status, message: e?.message });
      })
      .finally(() => { if (!cancelled) setRepairLoading(false); });
    return () => { cancelled = true; };
  }, [catalogParams, activeTab, offeringsLoaded, offerings.repair_capabilities_offered]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleKeywordChange = (e) => {
    const next = e.target.value;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next) p.set('keyword', next); else p.delete('keyword');
      p.set('page', '1');
      return p;
    });
  };

  const goPage = (nextPage) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('page', String(nextPage));
      return p;
    });
  };

  const submitParams = () => {
    const p = { page: '1', tab: activeTab };
    if (keyword) p.keyword = keyword;
    return p;
  };

  const setTab = (tab) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', tab);
      p.set('page', '1');
      p.delete('keyword');
      return p;
    });
  };

  const isAuthenticated = !!getSessionToken();

  // ── Derived display values ─────────────────────────────────────────────────
  const activeCatalog  = activeTab === TABS.REPAIR ? repairCatalog : partsCatalog;
  const activeLoading  = activeTab === TABS.REPAIR ? repairLoading : partsLoading;
  const activeError    = activeTab === TABS.REPAIR ? repairError   : partsError;

  const pageSize = activeCatalog.page_size || 25;
  const totalCountExact = activeCatalog.total_count_is_exact !== false;
  const totalCount = totalCountExact ? activeCatalog.total_count || 0 : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showTotalPageCount = totalCountExact && totalPages <= MAX_PAGES_SHOW_TOTAL;

  const resultsHeading = (() => {
    if (activeLoading) return 'Loading…';
    if (!totalCountExact) return activeCatalog.items.length ? 'Results' : 'No matches';
    if (totalCount === 0) return 'Results (0)';
    if (totalCount > RESULT_COUNT_CAP) return `Results (${RESULT_COUNT_CAP.toLocaleString()}+ — refine your search)`;
    return `Results (${totalCount.toLocaleString()})`;
  })();

  // ── Title / subtitle based on what's offered ───────────────────────────────
  const heroTitle = bothOffered
    ? 'Search our catalog…'
    : onlyRepair
    ? 'Search repair capabilities…'
    : 'Search our catalog…';

  const heroSubtitle = bothOffered
    ? 'Explore a comprehensive catalog of aircraft parts and repair capabilities. Drill into any result to view real-time inventory, sourcing options, and availability across on-hand stock, distribution networks, customer contracts, and approved alternates.'
    : onlyRepair
    ? 'Browse repair capability listings and request quotes for inspection, repair, or overhaul services.'
    : 'Explore aircraft parts inventory with real-time availability across on-hand stock, distribution networks, customer contracts, and approved alternates.';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <AppHeader />

      <main style={styles.main}>
        <div style={styles.container}>

          {/* ── Placeholder (neither offered) ── */}
          {offeringsLoaded && neitherOffered && (
            <div style={styles.placeholderCard}>
              <div style={styles.placeholderIcon}>🗂️</div>
              <h2 style={styles.placeholderTitle}>Catalogs are currently being loaded</h2>
              <p style={styles.placeholderText}>Check back soon to browse our offerings</p>
              <button type="button" style={styles.placeholderButton} onClick={() => navigate('/')}>
                Return Home
              </button>
            </div>
          )}

          {/* ── Search + results ── */}
          {offeringsLoaded && !neitherOffered && (
            <>
              <div style={styles.searchCard}>
                <h1 style={styles.title}>{heroTitle}</h1>
                <p style={styles.subtitle}>{heroSubtitle}</p>

                {/* Tabs */}
                {bothOffered && (
                  <div style={styles.tabRow}>
                    <button
                      type="button"
                      style={{ ...styles.tabBtn, ...(activeTab === TABS.PARTS ? styles.tabActive : {}) }}
                      onClick={() => setTab(TABS.PARTS)}
                    >
                      Parts
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.tabBtn, ...(activeTab === TABS.REPAIR ? styles.tabActive : {}) }}
                      onClick={() => setTab(TABS.REPAIR)}
                    >
                      Repair Capabilities
                    </button>
                  </div>
                )}

                <div style={styles.searchRow}>
                  <input
                    value={keyword}
                    onChange={handleKeywordChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') setSearchParams(submitParams()); }}
                    placeholder={activeTab === TABS.REPAIR ? 'Part number or description…' : 'Part number or description…'}
                    style={styles.input}
                  />
                  <button
                    type="button"
                    style={{ ...styles.primaryButton, backgroundColor: branding.primaryColor }}
                    onClick={() => setSearchParams(submitParams())}
                  >
                    Search
                  </button>
                </div>

                {activeError && <div style={styles.error}>{activeError}</div>}
              </div>

              {/* Results */}
              <div style={styles.resultsHeader}>
                <h2 style={styles.resultsTitle}>{resultsHeading}</h2>
                <Link to="/" style={styles.backLink}>← Home</Link>
              </div>

              {accessNotice && (
                <div style={styles.accessNotice} role="status">{accessNotice}</div>
              )}

              {/* Parts — a single flat list of available part numbers.
                  Pricing is not shown here; it is resolved live on the detail page. */}
              {activeTab === TABS.PARTS && (
                <>
                  <div style={styles.tableWrap}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.theadRow}>
                          <th style={styles.th}>PART</th>
                          <th style={styles.th}>KEYWORD</th>
                          <th style={styles.th}>CONDITIONS</th>
                          <th style={{ ...styles.th, ...styles.thQty }}>QTY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupPartRows(partsCatalog.items).map((group) => (
                          <tr
                            key={group.navId}
                            tabIndex={0}
                            role="button"
                            aria-label={`Open details for ${group.part_number}`}
                            style={{
                              ...styles.trClickable,
                              backgroundColor: hoveredRowId === group.navId ? 'var(--muted)' : 'transparent',
                              boxShadow: focusedRowId === group.navId
                                ? 'inset 0 0 0 2px var(--ring, rgba(59, 130, 246, 0.65))' : 'none',
                            }}
                            onMouseEnter={() => setHoveredRowId(group.navId)}
                            onMouseLeave={() => setHoveredRowId(null)}
                            onFocus={() => setFocusedRowId(group.navId)}
                            onBlur={() => setFocusedRowId(null)}
                            onClick={() => openPartDetail(group.navId)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openPartDetail(group.navId);
                              }
                            }}
                          >
                            <td style={styles.td}>{group.part_number}</td>
                            <td style={styles.td}>{group.description || '—'}</td>
                            <td style={styles.td}>
                              {group.conditions.length > 0
                                ? group.conditions.map((c) => (
                                    <span key={c} style={styles.condPill}>{c}</span>
                                  ))
                                : '—'}
                            </td>
                            <td style={{ ...styles.td, ...styles.tdQty }}>{group.total_qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!partsLoading && partsCatalog.items.length === 0 && !partsError && (
                    <p style={styles.muted}>No parts found. Try another search.</p>
                  )}
                </>
              )}

              {/* Repair table */}
              {activeTab === TABS.REPAIR && (
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.theadRow}>
                        <th style={styles.th}>PART</th>
                        <th style={styles.th}>KEYWORD</th>
                        <th style={styles.th}>INSPECTION</th>
                        <th style={styles.th}>REPAIR</th>
                        <th style={styles.th}>OVERHAUL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repairCatalog.items.map((row) => (
                        <tr
                          key={row.id}
                          tabIndex={0}
                          role="button"
                          aria-label={`Open repair details for ${row.part_number}`}
                          style={{
                            ...styles.trClickable,
                            backgroundColor: hoveredRepairId === row.id ? 'var(--muted)' : 'transparent',
                          }}
                          onMouseEnter={() => setHoveredRepairId(row.id)}
                          onMouseLeave={() => setHoveredRepairId(null)}
                          onClick={() => openRepairDetail(row.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openRepairDetail(row.id);
                            }
                          }}
                        >
                          <td style={styles.td}>{row.part_number}</td>
                          <td style={styles.td}>{row.keyword || '—'}</td>
                          <td style={styles.td}>
                            {repairServiceCell(row.inspection, row.inspection_amount, repairPricingDisplayed)}
                          </td>
                          <td style={styles.td}>
                            {repairServiceCell(row.repair, row.repair_amount, repairPricingDisplayed)}
                          </td>
                          <td style={styles.td}>
                            {repairServiceCell(row.overhaul, row.overhaul_amount, repairPricingDisplayed)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!repairLoading && repairCatalog.items.length === 0 && !repairError && (
                    <p style={styles.muted}>No repair capabilities found. Try another search.</p>
                  )}
                </div>
              )}

              {/* Pager */}
              <div style={styles.pager}>
                <button
                  type="button"
                  style={styles.pageBtn}
                  disabled={page <= 1 || activeLoading}
                  onClick={() => goPage(page - 1)}
                >
                  Previous
                </button>
                <span style={styles.pageInfo}>
                  Page {page}{showTotalPageCount ? ` of ${totalPages}` : ''}
                </span>
                <button
                  type="button"
                  style={styles.pageBtn}
                  disabled={!activeCatalog.has_more || activeLoading}
                  onClick={() => goPage(page + 1)}
                >
                  Next
                </button>
              </div>

              {!activeLoading && activeCatalog.items.length > 0
                && (!totalCountExact || totalCount > RESULT_COUNT_CAP || !showTotalPageCount) && (
                <p style={styles.muted}>
                  Large result sets are shown one page at a time. Add part numbers or keywords to narrow results.
                </p>
              )}
            </>
          )}
        </div>
      </main>

      {selectedRepairId && (
        <RepairDetailDialog
          itemId={selectedRepairId}
          branding={branding}
          pricingDisplayed={repairPricingDisplayed}
          isAuthenticated={isAuthenticated}
          onLoginRequired={(path) => openLogin({ redirectPath: path })}
          onClose={() => setSelectedRepairId(null)}
        />
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--background)', color: 'var(--foreground)' },
  main: { padding: '28px 24px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  placeholderCard: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '60px 24px', textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginTop: 12,
  },
  placeholderIcon: { fontSize: 48, marginBottom: 16 },
  placeholderTitle: { margin: 0, fontSize: 22, fontWeight: 700 },
  placeholderText: { margin: '10px 0 0', color: 'var(--muted-foreground)', fontSize: 16 },
  placeholderButton: {
    marginTop: 24, padding: '10px 24px', fontSize: 15, fontWeight: 600,
    backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)',
    border: 'none', borderRadius: 8, cursor: 'pointer',
  },
  searchCard: {
    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  title: { margin: 0, fontSize: 24, fontWeight: 700 },
  subtitle: { margin: '8px 0 0', color: 'var(--muted-foreground)' },
  tabRow: { display: 'flex', gap: 4, marginTop: 16, borderBottom: '2px solid var(--border)', paddingBottom: 0 },
  tabBtn: {
    padding: '8px 18px', border: 'none', background: 'none',
    cursor: 'pointer', fontWeight: 600, fontSize: 14, color: 'var(--muted-foreground)',
    borderBottom: '2px solid transparent', marginBottom: -2,
  },
  tabActive: { color: 'var(--foreground)', borderBottomColor: 'var(--foreground)' },
  searchRow: { display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' },
  input: {
    flex: 1, minWidth: 240, height: 42, borderRadius: 8,
    border: '1px solid var(--border)', backgroundColor: 'var(--input-background)', padding: '0 12px',
  },
  primaryButton: {
    height: 42, borderRadius: 8, border: 'none', padding: '0 14px',
    color: 'white', fontWeight: 700, cursor: 'pointer',
  },
  error: {
    marginTop: 12, padding: '10px 12px', borderRadius: 8,
    backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
    color: 'var(--destructive)',
  },
  resultsHeader: { marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  resultsTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  backLink: { color: 'var(--muted-foreground)', textDecoration: 'none', fontWeight: 600 },
  accessNotice: {
    marginTop: 14, padding: '12px 16px', borderRadius: 8,
    backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
    color: '#1e40af', fontSize: 14, fontWeight: 500,
  },
  tableWrap: { marginTop: 12, overflowX: 'auto' },
  table: {
    width: '100%', borderCollapse: 'collapse', backgroundColor: 'var(--card)',
    border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  theadRow: { backgroundColor: 'var(--secondary)' },
  th: {
    textAlign: 'left', padding: '11px 12px', borderBottom: '2px solid var(--border)',
    fontSize: 12, fontWeight: 800, letterSpacing: '0.06em',
    color: 'var(--foreground)', backgroundColor: '#f1f5f9',
  },
  thQty: { textAlign: 'right' },
  condPill: {
    display: 'inline-block',
    fontSize: 11, fontWeight: 700,
    padding: '2px 7px', borderRadius: 4,
    backgroundColor: 'var(--muted)',
    color: 'var(--muted-foreground)',
    marginRight: 4,
    letterSpacing: '0.03em',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: 14, verticalAlign: 'top' },
  tdQty: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  trClickable: {
    cursor: 'pointer', outline: 'none',
    transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
  },
  muted: { color: 'var(--muted-foreground)', marginTop: 16 },
  pager: { marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 },
  pageBtn: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
    background: 'var(--secondary)', cursor: 'pointer', fontWeight: 600,
  },
  pageInfo: { fontWeight: 600, color: 'var(--muted-foreground)' },
};

// ── Repair detail dialog ────────────────────────────────────────────────────

const DIALOG_SERVICES = [
  { key: 'inspection', label: 'Inspection', mro_service: 'inspection', amountKey: 'inspection_amount' },
  { key: 'repair',     label: 'Repair',     mro_service: 'repair',     amountKey: 'repair_amount'     },
  { key: 'overhaul',   label: 'Overhaul',   mro_service: 'overhaul',   amountKey: 'overhaul_amount'   },
];

function RepairDetailDialog({ itemId, branding, pricingDisplayed, isAuthenticated, onLoginRequired, onClose }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState({});
  const [toast, setToast] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setItem(null);
    getRepairCapabilityItem(decodeURIComponent(itemId))
      .then((data) => { if (!cancelled) setItem(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load capability'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Part image comes from the Product2 record, the same source and 'Part Image' file
  // type the parts tech-data modal uses, so both views show the same picture.
  const productId = item?.sf_product_id || '';
  useEffect(() => {
    if (!productId) { setImageUrl(null); return undefined; }
    let cancelled = false;
    let objectUrl = null;
    setImageLoading(true);
    (async () => {
      try {
        const data = await getFiles(productId);
        const list = Array.isArray(data) ? data : (data?.files || data?.records || []);
        const imgs = dedupeFiles(list.filter(isPartImage));
        if (!imgs.length) return;
        const url = await loadBlobUrl(fileVersionId(imgs[0]));
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setImageUrl(url);
      } catch {
        if (!cancelled) setImageUrl(null);
      } finally {
        if (!cancelled) setImageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [productId]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleAddToCart = async (svc) => {
    if (!isAuthenticated) {
      onLoginRequired(window.location.pathname);
      return;
    }
    setAdding((prev) => ({ ...prev, [svc.key]: true }));
    try {
      await addRepairToCart({
        catalog_kind: 'repair',
        part_number: item.part_number,
        sf_product_id: item.sf_product_id || undefined,
        sf_repair_capability_id: item.sf_repair_capability_id || undefined,
        description: item.keyword || item.part_number,
        mro_service: svc.mro_service,
      });
      window.dispatchEvent(new Event('cartUpdated'));
      track('cart.add', { kind: 'repair', service: svc.mro_service });
      setToast({ type: 'success', message: `${svc.label} service added to cart` });
    } catch (e) {
      logger.error('cart.add_failed', { kind: 'repair', service: svc.mro_service, status: e?.status, message: e?.message });
      setToast({ type: 'error', message: e?.message || 'Failed to add to cart' });
    } finally {
      setAdding((prev) => ({ ...prev, [svc.key]: false }));
    }
  };

  const offeredServices = item ? DIALOG_SERVICES.filter((s) => item[s.key] === true) : [];
  const quotableServices = offeredServices.filter(
    (s) => !(pricingDisplayed && item?.[s.amountKey] != null)
  );

  return (
    <div style={dialogStyles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dialogStyles.panel} role="dialog" aria-modal="true">

        {/* Header */}
        <div style={dialogStyles.header}>
          <h2 style={dialogStyles.title}>{item?.part_number || 'Repair Capability'}</h2>
          <button type="button" style={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ ...dialogStyles.toast, backgroundColor: toast.type === 'success' ? '#059669' : '#dc2626' }}>
            {toast.message}
          </div>
        )}

        {/* Body */}
        <div style={dialogStyles.body}>
          {loading && <p style={dialogStyles.muted}>Loading…</p>}
          {error && <p style={dialogStyles.error}>{error}</p>}
          {!loading && !error && item && (
            <>
              {item.keyword && <p style={dialogStyles.keyword}>{item.keyword}</p>}

              <div style={dialogStyles.summaryRow}>
                <div style={dialogStyles.imgBox}>
                  {imageUrl ? (
                    <img src={imageUrl} alt={`${item.part_number} part`} style={dialogStyles.imgEl} />
                  ) : (
                    <div style={dialogStyles.imgEmpty}>
                      <ImgPlaceholderSvg size={32} />
                      <span style={dialogStyles.imgEmptyText}>
                        {imageLoading ? 'Loading…' : 'No part image'}
                      </span>
                    </div>
                  )}
                </div>

                <div style={dialogStyles.metaRow}>
                  <div style={dialogStyles.metaCell}>
                    <span style={dialogStyles.metaLabel}>Part Number</span>
                    <span style={dialogStyles.metaValue}>{item.part_number}</span>
                  </div>
                </div>
              </div>

              <h3 style={dialogStyles.servicesTitle}>Available Services</h3>
              {offeredServices.length === 0 ? (
                <p style={dialogStyles.muted}>No services currently offered for this part.</p>
              ) : (
                <div style={dialogStyles.serviceGrid}>
                  {offeredServices.map((svc) => {
                    // A priced service is the end of the journey: the customer already has
                    // the number, so the cart and quote path is withdrawn for that service.
                    const amount = item[svc.amountKey];
                    const priced = pricingDisplayed && amount != null;
                    return (
                      <div key={svc.key} style={dialogStyles.serviceCard}>
                        <div style={dialogStyles.serviceName}>{svc.label}</div>
                        {priced ? (
                          <div style={dialogStyles.servicePrice}>{fmtPrice(amount)}</div>
                        ) : (
                          <button
                            type="button"
                            style={{ ...dialogStyles.addBtn, backgroundColor: branding.primaryColor }}
                            onClick={() => handleAddToCart(svc)}
                            disabled={!!adding[svc.key]}
                            aria-label={adding[svc.key] ? 'Adding…' : `Add ${svc.label} to cart`}
                            title={adding[svc.key] ? 'Adding…' : `Add ${svc.label} to cart`}
                          >
                            {adding[svc.key] ? (
                              '…'
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!isAuthenticated && quotableServices.length > 0 && (
                <p style={dialogStyles.muted}>
                  <button
                    type="button"
                    style={dialogStyles.loginLink}
                    onClick={() => onLoginRequired(window.location.pathname)}
                  >
                    Sign in
                  </button>{' '}
                  to request a repair quote.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const dialogStyles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1000,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  },
  panel: {
    backgroundColor: 'var(--card)', borderRadius: 12,
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    width: '100%', maxWidth: 560,
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    border: '1px solid var(--border)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px', borderBottom: '1px solid var(--border)',
  },
  title: { margin: 0, fontSize: 20, fontWeight: 800 },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 18, color: 'var(--muted-foreground)', lineHeight: 1,
    padding: '4px 6px', borderRadius: 6,
  },
  toast: {
    margin: '12px 20px 0',
    padding: '10px 16px', borderRadius: 8,
    color: 'white', fontWeight: 600, fontSize: 14,
  },
  body: { padding: '20px', overflowY: 'auto', flex: 1 },
  keyword: { margin: '0 0 16px', color: 'var(--muted-foreground)', fontSize: 15 },
  summaryRow: { display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 },
  imgBox: {
    width: 120, height: 120, flexShrink: 0,
    border: '1px solid var(--border)', borderRadius: 10,
    backgroundColor: 'var(--secondary)', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  imgEl: { width: '100%', height: '100%', objectFit: 'contain' },
  imgEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#9ca3af' },
  imgEmptyText: { marginTop: 6, fontSize: 11 },
  servicePrice: { fontWeight: 800, fontSize: 15, fontVariantNumeric: 'tabular-nums' },
  metaRow: { display: 'flex', gap: 28, flexWrap: 'wrap' },
  metaCell: { display: 'flex', flexDirection: 'column', gap: 2 },
  metaLabel: { fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: '0.06em', textTransform: 'uppercase' },
  metaValue: { fontSize: 15, fontWeight: 600 },
  servicesTitle: { margin: '0 0 12px', fontSize: 16, fontWeight: 700 },
  serviceGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
  serviceCard: {
    border: '1px solid var(--border)', borderRadius: 10,
    padding: '12px 16px', display: 'flex', flexDirection: 'row',
    alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: 'var(--secondary)',
  },
  serviceName: { fontWeight: 700, fontSize: 15 },
  addBtn: {
    border: 'none', borderRadius: 8, padding: '8px 10px',
    color: 'white', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    lineHeight: 1, flexShrink: 0,
  },
  muted: { color: 'var(--muted-foreground)', margin: '12px 0 0' },
  error: { color: 'var(--destructive)', margin: '12px 0 0' },
  loginLink: {
    background: 'none', border: 'none', padding: 0,
    cursor: 'pointer', color: 'inherit', fontWeight: 700, textDecoration: 'underline',
  },
};
